import { RolodexDocumentEntry } from 'features/files/types'
import {
  isExcelCsvDocument,
  isImageDocument,
  isPdfDocument,
} from 'features/files/utils/DocumentUtils'
import { sanitizeFilenameForRolodex } from 'features/files/utils/fileUploadUtils'
import { normalizeDocumentCategories } from 'features/files/utils/normalizeDocumentCategories'
import {
  useRolodexEntries,
  useRolodexFileLS,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryTagsType,
  RolodexEntryType,
  RolodexFileRef,
} from 'types'
import { z } from 'zod'

const optionalString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional()
)

/** Like optionalString but also accepts Date objects (e.g. from GeckoReport parsed data) */
const optionalDateString = z.preprocess((value) => {
  if (typeof value === 'string') return value
  if (value instanceof Date && !isNaN(value.getTime()))
    return value.toISOString()
  return undefined
}, z.string().optional())

const categoriesArray = z.preprocess((value) => {
  if (Array.isArray(value)) {
    return normalizeDocumentCategories(value)
  }

  if (typeof value === 'string' && value.length > 0) {
    return normalizeDocumentCategories([value])
  }

  return []
}, z.array(z.string()))

const documentEntryDataSchema = z
  .object({
    filename: optionalString,
    size: z.preprocess((value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value
      }
      if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10)
        return Number.isFinite(parsed) ? parsed : undefined
      }
      return undefined
    }, z.number().optional()),
    file_type: optionalString,
    categories: categoriesArray,
    upload_date: optionalDateString,
    document_date: optionalDateString,
    linked_entry_id: optionalString,
    fields: z.preprocess(
      (value) =>
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : undefined,
      z.record(z.string(), z.unknown()).optional()
    ),
    visible_name: optionalString,
    notes: optionalString,
    pageRotations: z.preprocess(
      (v) =>
        v && typeof v === 'object' && !Array.isArray(v)
          ? (v as Record<string, number>)
          : undefined,
      z.record(z.string(), z.number()).optional()
    ),
  })
  .passthrough()
/**
 * Process document entries into Document objects
 */
export const processDocumentEntries = (
  documentEntries: ReadonlyArray<GeckoEntry>
): ReadonlyArray<RolodexDocumentEntry> => {
  return documentEntries.map((entry: GeckoEntry) => {
    const data = documentEntryDataSchema.parse(entry.data ?? {})

    // Keep `filename` absent when the entry doesn't record one (entries created
    // via createGeckoReportData only write `visible_name`). Coercing to a
    // truthy placeholder here would defeat every `filename || visible_name`
    // display fallback downstream.
    const filename =
      typeof data.filename === 'string' && data.filename.length > 0
        ? data.filename
        : undefined

    const fileType =
      typeof data.file_type === 'string' && data.file_type.length > 0
        ? data.file_type
        : typeof data.type === 'string' && data.type.length > 0
          ? data.type
          : 'unknown'

    // TODO(talexander): remove this once the docs are ported over
    const legacyCategory =
      typeof data.category === 'string' && data.category.length > 0
        ? data.category
        : typeof data.file_category === 'string' &&
            data.file_category.length > 0
          ? data.file_category
          : undefined

    const normalizedCategories =
      data.categories.length > 0
        ? data.categories
        : legacyCategory
          ? normalizeDocumentCategories([legacyCategory])
          : []

    return {
      type: RolodexEntryType.REPORT,
      entry_id: entry.id,
      filename,
      size: data.size,
      file_type: fileType,
      categories: normalizedCategories,
      upload_date: data.upload_date,
      document_date: data.document_date,
      linked_entry_id: data.linked_entry_id ?? '',
      fields: data.fields ?? {},
      visible_name: data.visible_name,
      notes: data.notes,
      tags: entry.tags,
      pageRotations: data.pageRotations,
    }
  })
}

export const useLinkedDocuments = (
  tags: RolodexEntryTagsType | undefined
): {
  documents: ReadonlyArray<RolodexDocumentEntry>
  isLoading: boolean
  hasEverLoaded: boolean
  documentEntries: ReadonlyArray<GeckoEntry>
  forceRefresh: () => Promise<void>
} => {
  const queryParams = useMemo(
    () =>
      tags
        ? [
            {
              type: RolodexEntryType.REPORT,
              tags,
            },
          ]
        : undefined,
    [tags]
  )

  const {
    entries: documentEntries,
    isLoading: documentsLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryParams)

  // Fetch file references for all documents at once

  // Process document entries into Document objects with file references
  const documents = useMemo(() => {
    return processDocumentEntries(documentEntries)
  }, [documentEntries])

  return useMemo(
    () => ({
      documents,
      isLoading: documentsLoading,
      hasEverLoaded,
      documentEntries,
      forceRefresh,
    }),
    [documents, documentsLoading, documentEntries, forceRefresh, hasEverLoaded]
  )
}

/**
 * Pick the storage file backing a document entry from the entry's actual file
 * listing. `data.filename` is only a hint: several uploaders (anything built
 * on createGeckoReportData) never write it, and the blob path may instead be
 * the visible name or the entry id. Selection is deterministic so multi-file
 * entries always resolve to the same blob.
 */
export const chooseDocumentFileRef = (
  fileRefs: ReadonlyArray<RolodexFileRef>,
  document: Pick<
    RolodexDocumentEntry,
    'entry_id' | 'filename' | 'visible_name' | 'file_type'
  > | null
): RolodexFileRef | undefined => {
  if (!document) return undefined

  const files = fileRefs.filter((ref) => !ref.is_dir)
  if (files.length === 0) return undefined

  const byPath = (path: string | undefined) =>
    path ? files.find((ref) => ref.path === path) : undefined

  const exactMatch =
    byPath(document.filename) ??
    byPath(document.visible_name) ??
    byPath(
      document.visible_name
        ? sanitizeFilenameForRolodex(document.visible_name)
        : undefined
    ) ??
    byPath(document.entry_id)
  if (exactMatch) return exactMatch

  if (files.length === 1) return files[0]

  const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path))
  const extension = document.file_type?.toLowerCase()
  const extensionMatch = extension
    ? sorted.find((ref) => ref.path.toLowerCase().endsWith(`.${extension}`))
    : undefined
  return extensionMatch ?? sorted[0]
}

/**
 * Hook to get signed URLs for document previews.
 *
 * Resolves the real storage path by listing the entry's files rather than
 * trusting `data.filename` (same pattern as
 * assetLifecycleV2's useReportEntrySignedUrl). `filePath` is resolved for any
 * file type; the signed URL is only fetched for previewable types.
 */
export const useDocumentSignedUrls = (
  document: RolodexDocumentEntry | null
) => {
  const isPdf = useMemo(() => isPdfDocument(document), [document])
  const isImage = useMemo(() => isImageDocument(document), [document])
  const isExcelCsv = useMemo(() => isExcelCsvDocument(document), [document])

  const entryId = document?.entry_id
  const lsQuery = useMemo(
    () => (entryId ? { entry_id: entryId, path: '**' } : undefined),
    [entryId]
  )
  const { fileRefs, isLoading: refsLoading } = useRolodexFileLS(lsQuery)

  const filename = document?.filename
  const visibleName = document?.visible_name
  const fileType = document?.file_type
  const chosenRef = useMemo(
    () =>
      entryId
        ? chooseDocumentFileRef(fileRefs, {
            entry_id: entryId,
            filename,
            visible_name: visibleName,
            file_type: fileType,
          })
        : undefined,
    [fileRefs, entryId, filename, visibleName, fileType]
  )

  const urlParams = useMemo(
    () =>
      chosenRef && (isPdf || isImage || isExcelCsv)
        ? [{ entry_id: chosenRef.entry_id, path: chosenRef.path }]
        : undefined,
    [chosenRef, isPdf, isImage, isExcelCsv]
  )

  const { url, isLoading: urlsLoading } = useRolodexSignedURLs(urlParams)

  return {
    // A per-path 404 yields an undefined slot inside a successful result, so
    // don't infer success from the response shape.
    signedUrl: url || undefined,
    filePath: chosenRef?.path,
    isLoading: refsLoading || urlsLoading,
    isPdf,
    isImage,
    isExcelCsv,
  }
}
